import { Search } from "lucide-react";
import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "../../input/input-group";

export interface TextFilter {
  type: "text";
  value: string;
}

interface TextFilterInputProps {
  value: TextFilter | undefined;
  onChange: (value: TextFilter | undefined) => void;
  placeholder?: string;
}

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

export function TextFilterInput({
  value,
  onChange,
  placeholder = "Search…",
}: TextFilterInputProps) {
  const [localValue, setLocalValue] = useState(value?.value ?? "");
  const debounced = useDebounce(localValue);

  // Store onChange in a ref so we never need it as an effect dependency.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // Only notify parent after the initial render to avoid triggering on mount
  // with an empty value (which would create a new filters object each cycle).
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (debounced) {
      onChangeRef.current({ type: "text", value: debounced });
    } else {
      onChangeRef.current(undefined);
    }
  }, [debounced]);

  // Sync when the external value is cleared (e.g. badge dismissed)
  useEffect(() => {
    setLocalValue(value?.value ?? "");
  }, [value?.value]);

  return (
    <InputGroup className="h-8 min-h-8">
      <InputGroupAddon className="py-0">
        <Search size={14} className="text-gray-500" />
      </InputGroupAddon>
      <InputGroupInput
        data-filter-search
        className="h-8 text-sm"
        value={localValue}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setLocalValue(e.target.value)
        }
        placeholder={placeholder}
      />
    </InputGroup>
  );
}
